import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { InlineList, InlineListRow } from '@/components/InlineList';
import { Modal } from '@/components/Modal';
import type { AdminSubjectsResponse, SubjectDocumentDto } from '@/types/admin';
import type { User } from '@/types/auth';
import { ChevronDown, ChevronUp, Copy, Download, FileText, GripVertical, Pencil, Trash2, Upload, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import './AdminSubjects.css';
import '../styles/common.css';

const AdminSubjects: React.FC = () => {
  const [subjects, setSubjects] = useState<AdminSubjectsResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null);
  const [editingSubject, setEditingSubject] = useState<AdminSubjectsResponse | null>(null);
  const [isCreatingSubject, setIsCreatingSubject] = useState(false);
  const [deletingSubject, setDeletingSubject] = useState<AdminSubjectsResponse | null>(null);
  const [subjectForm, setSubjectForm] = useState({ number: '', title: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [duplicatingSubjectId, setDuplicatingSubjectId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [inlineEditingSubjectId, setInlineEditingSubjectId] = useState<string | null>(null);
  const [inlineEditMeta, setInlineEditMeta] = useState('');
  const [inlineEditDependencies, setInlineEditDependencies] = useState<string[]>([]);
  const [inlineEditSaving, setInlineEditSaving] = useState(false);
  const [inlineEditError, setInlineEditError] = useState<string | null>(null);
  const [dependencySearch, setDependencySearch] = useState('');
  const [dependencyDropdownOpen, setDependencyDropdownOpen] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [documentDragOver, setDocumentDragOver] = useState(false);
  const [draggedSubjectId, setDraggedSubjectId] = useState<string | null>(null);
  const [dragOverSubjectId, setDragOverSubjectId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'before' | 'after'>('before');
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const listRef = useRef<HTMLDivElement>(null);
  const dragImageRef = useRef<HTMLDivElement | null>(null);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const dependencyDropdownRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  
  const moduleId = searchParams.get('moduleId');
  const from = searchParams.get('from') || 'modules';

  useEffect(() => {
    fetchUserInfo();
    if (moduleId) {
      fetchSubjects();
    } else {
      setError('No module ID provided');
      setLoading(false);
    }
  }, [moduleId]);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const focusNumberInput = () => {
    requestAnimationFrame(() => {
      numberInputRef.current?.focus();
      numberInputRef.current?.select();
    });
  };

  useEffect(() => {
    if (!editingSubject && !isCreatingSubject) return;

    focusNumberInput();
  }, [editingSubject, isCreatingSubject]);

  const fetchSubjects = async () => {
    if (!moduleId) return;
    
    try {
      setLoading(true);
      setError(null);
      const subjectsData = await adminService.getSubjectsByModule(moduleId);
      setSubjects(subjectsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };


  const getSubjectDependencies = (subject: AdminSubjectsResponse): string[] => {
    return subject.dependencySubjectIds ?? [];
  };

  const startInlineEdit = (subject: AdminSubjectsResponse) => {
    setInlineEditError(null);
    setInlineEditMeta(subject.meta ?? '');
    setInlineEditDependencies(getSubjectDependencies(subject));
    setDependencySearch('');
    setDependencyDropdownOpen(false);
    setInlineEditingSubjectId(subject.id);
  };

  const cancelInlineEdit = () => {
    setInlineEditingSubjectId(null);
    setInlineEditMeta('');
    setInlineEditDependencies([]);
    setDependencySearch('');
    setDependencyDropdownOpen(false);
    setInlineEditError(null);
  };

  const handleSaveInlineEdit = async () => {
    if (!inlineEditingSubjectId) return;

    const subject = subjects.find(s => s.id === inlineEditingSubjectId);
    if (!subject) return;

    try {
      setInlineEditSaving(true);
      setInlineEditError(null);

      await adminService.updateSubject(subject.id, {
        number: subject.number,
        title: subject.title,
        meta: inlineEditMeta,
        dependencySubjectIds: inlineEditDependencies,
      });

      setSubjects(currentSubjects => currentSubjects.map(s => (
        s.id === subject.id
          ? { ...s, meta: inlineEditMeta, dependencySubjectIds: inlineEditDependencies }
          : s
      )));
      setInlineEditingSubjectId(null);
    } catch (err) {
      setInlineEditError(err instanceof Error ? err.message : 'Saving failed');
    } finally {
      setInlineEditSaving(false);
    }
  };

  const addDependency = (subjectId: string) => {
    setInlineEditDependencies(current => current.includes(subjectId) ? current : [...current, subjectId]);
    setDependencySearch('');
  };

  const removeDependency = (subjectId: string) => {
    setInlineEditDependencies(current => current.filter(id => id !== subjectId));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dependencyDropdownOpen &&
        dependencyDropdownRef.current &&
        !dependencyDropdownRef.current.contains(event.target as Node)
      ) {
        setDependencyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dependencyDropdownOpen]);

  const toggleExpanded = (subjectId: string) => {
    setExpandedSubject(expandedSubject === subjectId ? null : subjectId);
  };

  const openEditModal = (subject: AdminSubjectsResponse) => {
    setModalError(null);
    setSubjectForm({ number: subject.number, title: subject.title });
    setEditingSubject(subject);
  };

  const openCreateModal = () => {
    setModalError(null);
    setSubjectForm({ number: '', title: '' });
    setIsCreatingSubject(true);
  };

  const closeSubjectModal = () => {
    if (isSaving) return;

    setEditingSubject(null);
    setIsCreatingSubject(false);
    setModalError(null);
  };

  const handleSaveSubject = async (saveAndNew = false) => {
    try {
      setIsSaving(true);
      setModalError(null);

      if (editingSubject) {
        await adminService.updateSubject(editingSubject.id, subjectForm);
        setSubjects(currentSubjects => currentSubjects.map(subject => (
          subject.id === editingSubject.id
            ? { ...subject, number: subjectForm.number.trim(), title: subjectForm.title.trim() }
            : subject
        )));
        setEditingSubject(null);
        return;
      }

      if (!isCreatingSubject || !moduleId) return;

      const subject = await adminService.createSubject(moduleId, subjectForm);
      setSubjects(currentSubjects => [...currentSubjects, subject]);

      if (saveAndNew) {
        setSubjectForm({ number: '', title: '' });
        focusNumberInput();
      } else {
        setIsCreatingSubject(false);
      }
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Subject could not be saved');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicateSubject = async (subject: AdminSubjectsResponse) => {
    try {
      setDuplicatingSubjectId(subject.id);
      setActionError(null);
      const duplicate = await adminService.duplicateSubject(subject.id);
      setSubjects(currentSubjects => {
        const sourceIndex = currentSubjects.findIndex(currentSubject => currentSubject.id === subject.id);
        if (sourceIndex === -1) return [...currentSubjects, duplicate];

        return [
          ...currentSubjects.slice(0, sourceIndex + 1),
          duplicate,
          ...currentSubjects.slice(sourceIndex + 1),
        ];
      });
      openEditModal(duplicate);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Subject could not be duplicated');
    } finally {
      setDuplicatingSubjectId(null);
    }
  };

  const handleDeleteSubject = async () => {
    if (!deletingSubject) return;

    try {
      setIsSaving(true);
      setModalError(null);
      await adminService.deleteSubject(deletingSubject.id);
      setSubjects(currentSubjects => currentSubjects.filter(subject => subject.id !== deletingSubject.id));
      setExpandedSubject(currentSubjectId => currentSubjectId === deletingSubject.id ? null : currentSubjectId);
      setDeletingSubject(null);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Subject could not be deleted');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDocumentUpload = useCallback(async (subjectId: string, files: FileList | File[]) => {
    setDocumentError(null);
    setDocumentUploading(true);

    try {
      const uploaded: SubjectDocumentDto[] = [];
      for (const file of Array.from(files)) {
        const doc = await adminService.uploadSubjectDocument(subjectId, file);
        uploaded.push(doc);
      }
      setSubjects(current => current.map(s =>
        s.id === subjectId
          ? { ...s, documents: [...(s.documents ?? []), ...uploaded] }
          : s
      ));
    } catch (err) {
      setDocumentError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setDocumentUploading(false);
    }
  }, []);

  const handleDocumentDelete = useCallback(async (subjectId: string, documentId: string) => {
    setDocumentError(null);
    try {
      await adminService.deleteSubjectDocument(subjectId, documentId);
      setSubjects(current => current.map(s =>
        s.id === subjectId
          ? { ...s, documents: (s.documents ?? []).filter(d => d.id !== documentId) }
          : s
      ));
    } catch (err) {
      setDocumentError(err instanceof Error ? err.message : 'Delete failed');
    }
  }, []);

  const handleDocumentDownload = useCallback(async (subjectId: string, doc: SubjectDocumentDto) => {
    const token = localStorage.getItem('token');
    const url = adminService.getSubjectDocumentDownloadUrl(subjectId, doc.id);
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setDocumentError(err instanceof Error ? err.message : 'Download failed');
    }
  }, []);

  const handleDocDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDocumentDragOver(true);
  }, []);

  const handleDocDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDocumentDragOver(false);
  }, []);

  const handleDocDrop = useCallback((e: React.DragEvent, subjectId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDocumentDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      void handleDocumentUpload(subjectId, e.dataTransfer.files);
    }
  }, [handleDocumentUpload]);

  const handleDragStart = (event: React.DragEvent<HTMLSpanElement>, subjectId: string) => {
    setDraggedSubjectId(subjectId);

    const row = rowRefs.current[subjectId];
    if (!row || !event.dataTransfer) return;

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', subjectId);

    const clone = row.cloneNode(true) as HTMLDivElement;
    clone.classList.remove('subject-row-dragging', 'subject-row-over-before', 'subject-row-over-after');
    clone.querySelector('.inline-list-row-actions')?.remove();
    clone.style.position = 'fixed';
    clone.style.top = '-9999px';
    clone.style.left = '-9999px';
    clone.style.width = `${row.offsetWidth}px`;
    clone.style.opacity = '1';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '-1';
    clone.classList.add('subject-drag-image');
    document.body.appendChild(clone);

    const rowRect = row.getBoundingClientRect();
    const offsetX = event.clientX - rowRect.left;
    const offsetY = event.clientY - rowRect.top;
    event.dataTransfer.setDragImage(clone, offsetX, offsetY);
    dragImageRef.current = clone;
  };

  const computeInsertIndex = (clientY: number) => {
    for (let i = 0; i < subjects.length; i++) {
      const row = rowRefs.current[subjects[i].id];
      if (!row) continue;

      const rect = row.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      if (clientY < middle) return i;
    }
    return subjects.length;
  };

  const updateDragOverIndicator = (clientY: number) => {
    if (subjects.length === 0) return;

    const insertIndex = computeInsertIndex(clientY);
    if (insertIndex === 0) {
      setDragOverSubjectId(subjects[0].id);
      setDragOverPosition('before');
    } else if (insertIndex >= subjects.length) {
      setDragOverSubjectId(subjects[subjects.length - 1].id);
      setDragOverPosition('after');
    } else {
      setDragOverSubjectId(subjects[insertIndex].id);
      setDragOverPosition('before');
    }
  };

  const handleListDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggedSubjectId) return;
    updateDragOverIndicator(event.clientY);
  };

  const handleListDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (listRef.current && listRef.current.contains(event.relatedTarget as Node)) return;
    setDragOverSubjectId(null);
  };

  const handleListDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggedSubjectId) {
      setDragOverSubjectId(null);
      return;
    }

    const insertIndex = computeInsertIndex(event.clientY);
    const sourceIndex = subjects.findIndex(s => s.id === draggedSubjectId);
    if (sourceIndex === -1) {
      setDraggedSubjectId(null);
      setDragOverSubjectId(null);
      return;
    }

    if (sourceIndex === insertIndex || sourceIndex + 1 === insertIndex) {
      setDraggedSubjectId(null);
      setDragOverSubjectId(null);
      return;
    }

    const newSubjects = [...subjects];
    const [moved] = newSubjects.splice(sourceIndex, 1);
    let targetIndex = insertIndex;
    if (sourceIndex < targetIndex) targetIndex--;
    newSubjects.splice(targetIndex, 0, moved);

    setSubjects(newSubjects);
    setDraggedSubjectId(null);
    setDragOverSubjectId(null);

    if (moduleId) {
      adminService.reorderSubjects(moduleId, { subjectIds: newSubjects.map(s => s.id) })
        .catch((err) => {
          setActionError(err instanceof Error ? err.message : 'Order could not be saved');
        });
    }
  };

  const handleDragEnd = () => {
    setDraggedSubjectId(null);
    setDragOverSubjectId(null);
    dragImageRef.current?.remove();
    dragImageRef.current = null;
  };

  const getRowClassName = (subjectId: string) => {
    const classes: string[] = [];
    if (draggedSubjectId === subjectId) {
      classes.push('subject-row-dragging');
    }
    if (dragOverSubjectId === subjectId) {
      classes.push(dragOverPosition === 'before' ? 'subject-row-over-before' : 'subject-row-over-after');
    }
    return classes.join(' ');
  };

  return (
    <div className="admin-subjects-container">
      <AppHeader 
        user={user} 
        title="KI Expert - Admin" 
        subtitle={`Subjects for Module: ${moduleId}`} 
      />
      
      <main className="list-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              from === 'classes' ? { label: 'Classes', path: '/admin/classes' } : { label: 'Modules', path: '/admin/modules' },
              { label: 'Subjects' },
            ]} />
            <h2 className="navigation-bar-title">Subjects</h2>
          </div>
          <div className="navigation-bar-actions">
            <button className="btn btn-success" onClick={openCreateModal} type="button">
              Add Subject
            </button>
          </div>
        </div>

        {actionError && <p className="subject-action-error">{actionError}</p>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading subjects...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchSubjects} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : subjects.length === 0 ? (
          <div className="empty-container">
            <div className="empty-icon">📝</div>
            <h3>No subjects found</h3>
            <p>No subjects are available for this module.</p>
          </div>
        ) : (
          <InlineList
            compact
            ref={listRef}
            onDragLeave={handleListDragLeave}
            onDragOver={handleListDragOver}
            onDrop={handleListDrop}
          >
            {subjects.map(subject => (
              <React.Fragment key={subject.id}>
                <InlineListRow
                  actions={(
                    <>
                    <button
                      aria-label="Edit subject"
                      className="subject-action"
                      onClick={(event) => {
                        event.stopPropagation();
                        openEditModal(subject);
                      }}
                      title="Edit"
                      type="button"
                    >
                      <Pencil aria-hidden="true" size={18} />
                    </button>
                    <button
                      aria-label="Duplicate subject"
                      className="subject-action"
                      disabled={duplicatingSubjectId === subject.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDuplicateSubject(subject);
                      }}
                      title="Duplicate"
                      type="button"
                    >
                      <Copy aria-hidden="true" size={18} />
                    </button>
                    <button
                      aria-label="Delete subject"
                      className="subject-action subject-action-danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        setModalError(null);
                        setDeletingSubject(subject);
                      }}
                      title="Delete"
                      type="button"
                    >
                      <Trash2 aria-hidden="true" size={18} />
                    </button>
                    <span aria-hidden="true" className="subject-expand-indicator">
                      {expandedSubject === subject.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </span>
                    </>
                  )}
                  className={getRowClassName(subject.id)}
                  dragHandle={(
                    <span
                      className="subject-drag-handle"
                      draggable
                      onClick={(event) => event.stopPropagation()}
                      onDragEnd={handleDragEnd}
                      onDragStart={(event) => handleDragStart(event, subject.id)}
                      title="Drag to reorder"
                    >
                      <GripVertical aria-hidden="true" size={18} />
                    </span>
                  )}
                  onClick={() => toggleExpanded(subject.id)}
                  rowRef={(node) => { rowRefs.current[subject.id] = node; }}
                >
                  <span className="subject-number">{subject.number}</span>
                  <h3 className="subject-title">{subject.title}</h3>
                </InlineListRow>

                {expandedSubject === subject.id && (
                  <div className="subject-details">
                    <div className="admin-subject-meta">
                      <h4>Meta Prompt:</h4>
                      {inlineEditingSubjectId === subject.id ? (
                        <textarea
                          className="meta-editable"
                          value={inlineEditMeta}
                          onChange={(event) => setInlineEditMeta(event.target.value)}
                          rows={12}
                        />
                      ) : (
                        <p className="meta-readonly">{subject.meta}</p>
                      )}
                    </div>

                    {(inlineEditingSubjectId === subject.id || getSubjectDependencies(subject).length > 0) && (
                    <div className="admin-subject-dependency">
                      <h4>Dependency:</h4>
                      {inlineEditingSubjectId === subject.id ? (
                        <div className="dependency-select" ref={dependencyDropdownRef}>
                          <div className="dependency-input-box">
                            {inlineEditDependencies.map(depId => {
                              const dep = subjects.find(s => s.id === depId);
                              return (
                                <span key={depId} className="dependency-chip">
                                  <span className="dependency-chip-text">{dep ? `${dep.number} ${dep.title}` : depId}</span>
                                  <button
                                    aria-label="Remove dependency"
                                    className="dependency-chip-remove"
                                    disabled={inlineEditSaving}
                                    onClick={() => removeDependency(depId)}
                                    type="button"
                                  >
                                    <X size={14} />
                                  </button>
                                </span>
                              );
                            })}
                            <input
                              className="dependency-search-input"
                              disabled={inlineEditSaving}
                              onChange={(event) => setDependencySearch(event.target.value)}
                              onFocus={() => setDependencyDropdownOpen(true)}
                              placeholder="Search subject..."
                              type="text"
                              value={dependencySearch}
                            />
                          </div>
                          {(() => {
                            const available = subjects.filter(
                              s =>
                                s.id !== subject.id &&
                                !inlineEditDependencies.includes(s.id) &&
                                (
                                  s.title.toLowerCase().includes(dependencySearch.toLowerCase()) ||
                                  s.number.toLowerCase().includes(dependencySearch.toLowerCase())
                                )
                            );
                            return dependencyDropdownOpen && available.length > 0 ? (
                              <ul className="dependency-dropdown">
                                {available.map(s => (
                                  <li key={s.id} onClick={() => addDependency(s.id)}>
                                    <span className="subject-number-sm">{s.number}</span>
                                    <span className="subject-title-sm">{s.title}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <div className="dependency-display">
                          {getSubjectDependencies(subject).length > 0 ? (
                            <div className="dependency-chips">
                              {getSubjectDependencies(subject).map(depId => {
                                const dep = subjects.find(s => s.id === depId);
                                return (
                                  <span key={depId} className="dependency-chip readonly">
                                    <span className="dependency-chip-text">{dep ? `${dep.number} ${dep.title}` : depId}</span>
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="dependency-empty">No dependencies</p>
                          )}
                        </div>
                      )}
                    </div>
                    )}

                    <div className="admin-subject-documents">
                      <h4>Documents:</h4>

                      {/* Upload-Bereich (Drag & Drop) - nur im Edit-Modus */}
                      {inlineEditingSubjectId === subject.id && (
                        <div
                          className={`document-dropzone${documentDragOver ? ' dragover' : ''}`}
                          onDragOver={handleDocDragOver}
                          onDragLeave={handleDocDragLeave}
                          onDrop={(e) => handleDocDrop(e, subject.id)}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = '.md,.txt,.pdf,.png,.jpg,.jpeg,.tf,.json,.yaml,.yml,.csv';
                            input.onchange = () => {
                              if (input.files && input.files.length > 0) {
                                void handleDocumentUpload(subject.id, input.files);
                              }
                            };
                            input.click();
                          }}
                        >
                          <Upload size={24} className="document-dropzone-icon" />
                          <span className="document-dropzone-text">
                            {documentUploading ? 'Uploading...' : 'Drag files here or click'}
                          </span>
                        </div>
                      )}

                      {documentError && <p className="document-error">{documentError}</p>}

                      {/* Liste der hochgeladenen Dokumente */}
                      {(subject.documents ?? []).length > 0 ? (
                        <ul className="document-list">
                          {(subject.documents ?? []).map(doc => (
                            <li key={doc.id} className="document-item">
                              <FileText size={16} className="document-item-icon" />
                              <span className="document-item-name">{doc.fileName}</span>
                              <span className="document-item-size">
                                {doc.sizeBytes < 1024
                                  ? `${doc.sizeBytes} B`
                                  : doc.sizeBytes < 1024 * 1024
                                    ? `${(doc.sizeBytes / 1024).toFixed(1)} KB`
                                    : `${(doc.sizeBytes / (1024 * 1024)).toFixed(1)} MB`}
                              </span>
                              <button
                                className="document-action"
                                onClick={() => void handleDocumentDownload(subject.id, doc)}
                                title="Download"
                                type="button"
                              >
                                <Download size={16} />
                              </button>
                              {inlineEditingSubjectId === subject.id && (
                                <button
                                  className="document-action document-action-danger"
                                  onClick={() => void handleDocumentDelete(subject.id, doc.id)}
                                  title="Delete"
                                  type="button"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        inlineEditingSubjectId !== subject.id && (
                          <p className="document-empty">No documents</p>
                        )
                      )}
                    </div>

                    <div className="admin-subject-actions">
                      {inlineEditingSubjectId === subject.id ? (
                        <>
                          <button
                            className="btn btn-secondary"
                            disabled={inlineEditSaving}
                            onClick={cancelInlineEdit}
                            type="button"
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            disabled={inlineEditSaving}
                            onClick={handleSaveInlineEdit}
                            type="button"
                          >
                            {inlineEditSaving ? 'Saving...' : 'Save'}
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-secondary"
                          onClick={() => startInlineEdit(subject)}
                          type="button"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {inlineEditError && inlineEditingSubjectId === subject.id && (
                      <p className="inline-edit-error">{inlineEditError}</p>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </InlineList>
        )}
      </main>

      {(editingSubject || isCreatingSubject) && (
        <Modal
          footer={isCreatingSubject ? (
            <>
              <button className="btn btn-secondary" disabled={isSaving} onClick={closeSubjectModal} type="button">
                Cancel
              </button>
              <button className="btn btn-secondary" disabled={isSaving} onClick={() => handleSaveSubject(true)} type="button">
                {isSaving ? 'Saving...' : 'Save and New'}
              </button>
              <button className="btn btn-primary" disabled={isSaving} onClick={() => handleSaveSubject()} type="button">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" disabled={isSaving} onClick={closeSubjectModal} type="button">
                Cancel
              </button>
              <button className="btn btn-primary" disabled={isSaving} onClick={() => handleSaveSubject()} type="button">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
          onClose={closeSubjectModal}
          title={isCreatingSubject ? 'Add Subject' : 'Edit subject'}
        >
          {modalError && <p className="modal-error">{modalError}</p>}
          <div className="modal-field">
            <label htmlFor="subject-reference">Reference</label>
            <input
              id="subject-reference"
              maxLength={8}
              onChange={(event) => setSubjectForm(currentForm => ({ ...currentForm, number: event.target.value }))}
              ref={numberInputRef}
              value={subjectForm.number}
            />
          </div>
          <div className="modal-field">
            <label htmlFor="subject-title">Subject text</label>
            <input
              id="subject-title"
              onChange={(event) => setSubjectForm(currentForm => ({ ...currentForm, title: event.target.value }))}
              value={subjectForm.title}
            />
          </div>
        </Modal>
      )}

      {deletingSubject && (
        <Modal
          footer={(
            <>
              <button className="btn btn-secondary" disabled={isSaving} onClick={() => setDeletingSubject(null)} type="button">
                Cancel
              </button>
              <button className="btn btn-danger" disabled={isSaving} onClick={handleDeleteSubject} type="button">
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}
          onClose={() => !isSaving && setDeletingSubject(null)}
          title="Delete subject"
        >
          {modalError && <p className="modal-error">{modalError}</p>}
          <p className="modal-confirmation-message">
            Should the subject “{deletingSubject.number} – {deletingSubject.title}” really be deleted?
          </p>
        </Modal>
      )}

    </div>
  );
};

export default AdminSubjects;
